"use client";

import { FC } from "react";

interface OpenInterestDisplayProps {
  callOi: number | null;
  putOi: number | null;
  callLabel?: string;
  putLabel?: string;
  errorMessage?: string;
  fallbackValue?: string;
}

const formatNumber = (num: number): string => {
  if (num >= 10000000) {
    return `${(num / 10000000).toFixed(2)} Cr`;
  }
  if (num >= 100000) {
    return `${(num / 100000).toFixed(2)} L`;
  }
  return num.toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  });
};

const normalizeOi = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

export const OpenInterestDisplay: FC<OpenInterestDisplayProps> = ({
  callOi,
  putOi,
  callLabel,
  putLabel,
  errorMessage,
  fallbackValue,
}) => {
  const safeCallOi = normalizeOi(callOi);
  const safePutOi = normalizeOi(putOi);
  const hasError = safeCallOi === null || safePutOi === null;
  const resolvedCallLabel = callLabel ?? "Call Open Interest";
  const resolvedPutLabel = putLabel ?? "Put Open Interest";
  const resolvedErrorMessage = errorMessage ?? "Failed to fetch";
  const resolvedFallbackValue = fallbackValue ?? "-";

  return (
    <div className="flex flex-col gap-[8px]">
      <div className="flex items-center justify-between">
        <span className="md:text-[14px] text-[12px] text-[#1a1a1a] font-[400px]">
          {resolvedCallLabel}
        </span>
        {hasError ? (
          <span className="tmd:text-[14px] text-[12px]font-medium text-[#B40000]">
            {resolvedErrorMessage}
          </span>
        ) : (
          <span className="md:text-[14px] text-[12px] font-[400px] text-[#1a1a1a]">
            {safeCallOi !== null && safeCallOi !== undefined
              ? formatNumber(safeCallOi)
              : resolvedFallbackValue}
          </span>
        )}
      </div>
      <div className="flex items-center justify-between">
        <span className="md:text-[14px] text-[12px] text-[#1a1a1a] font-[400px]">
          {resolvedPutLabel}
        </span>
        {hasError ? (
          <span className="md:text-[14px] text-[12px] font-medium text-[#B40000]">
            {resolvedErrorMessage}
          </span>
        ) : (
          <span className="md:text-[14px] text-[12px] font-[400px] text-[#1a1a1a]">
            {safePutOi !== null && safePutOi !== undefined
              ? formatNumber(safePutOi)
              : resolvedFallbackValue}
          </span>
        )}
      </div>
      {hasError && (
        <div className="border-t border-gray-200 pt-3">
          <span className="text-sm font-medium text-red-500">
            {resolvedErrorMessage}
          </span>
        </div>
      )}
    </div>
  );
};